import React from 'react';
import { useAppSelector } from '../../../../store/reducers/store';
import { Box, ITheme, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Utilities } from '../../../../utility/utils';






const InvestmentsGraph: React.FC = () => {
    const width = 550;
    const height = 200;
    const maxHeight = 150; // Highest bar of the graph
    const leftMargin = 40;
    const barWidth = 36; // Horizontal size of the bar
    const barSpacing = 60; // Space between the bars
    const theme = useTheme() as ITheme;
    const { i18n } = useTranslation();

    const renderAxisLabel = (pos: number[], text: string, textColor: string): React.ReactNode => {
        return <foreignObject width={"50px"} height={"30px"} x={pos[0] - 35} y={pos[1] - 15} key={`axis-label-${text}`}>
            <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                <Typography fontSize={"8px"} fontWeight={"400"} lineHeight={"9.75px"} textAlign={"right"} color={textColor}>{text}</Typography>
            </Box>
        </foreignObject>
    }

    const renderLabel = (pos: number[], proportion: number, textColor: string, backgroundColor: string): React.ReactNode => {
        const x = pos[0]
        let y = pos[1];
        if (y > -10) y -= 20
        return <foreignObject width={"60px"} height={"30px"} x={x - 30} y={y - 15}>
            <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Box display={"flex"} padding={"2px 6px"} minWidth={"28px"} bgcolor={backgroundColor} borderRadius={"10px"} justifyContent={"center"} alignItems={"center"}>
                    <Typography fontSize={"14px"} fontWeight={"600"} lineHeight={"17.07px"} color={textColor}>{proportion.toLocaleString(i18n.language)}</Typography>
                </Box>
            </Box>
        </foreignObject>
    }

    const renderBottomLabel = (pos: number[], proportion: number, textColor: string): React.ReactNode => {
        return <foreignObject width={"80px"} height={"30px"} x={pos[0] - 40} y={pos[1] - 15}>
            <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Typography fontSize={"16px"} fontWeight={"600"} lineHeight={"18px"} color={textColor}>€{proportion.toLocaleString(i18n.language)}</Typography>
            </Box>
        </foreignObject>
    }

    const investors = useAppSelector(state => state.portfolioDetails.portfolio?.tickets);

    if (investors === undefined) {
        return <></>;
    }
    const cohort1 = investors.filter(i => i.amount < 300);
    const cohort2 = investors.filter(i => i.amount >= 300 && i.amount < 1000);
    const cohort3 = investors.filter(i => i.amount >= 1000 && i.amount < 4000);
    const cohort4 = investors.filter(i => i.amount >= 4000 && i.amount < 10000);
    const cohort5 = investors.filter(i => i.amount >= 10000);

    const valueCat1 = cohort1.reduce((acc, currentValue) => (acc + currentValue.amount), 0);
    const valueCat2 = cohort2.reduce((acc, currentValue) => (acc + currentValue.amount), 0);
    const valueCat3 = cohort3.reduce((acc, currentValue) => (acc + currentValue.amount), 0);
    const valueCat4 = cohort4.reduce((acc, currentValue) => (acc + currentValue.amount), 0);
    const valueCat5 = cohort5.reduce((acc, currentValue) => (acc + currentValue.amount), 0);
    // const total = investors.length;

    let highest = Math.max(valueCat1, valueCat2, valueCat3, valueCat4, valueCat5);

    const searchedMultiple = 1000;
    const reste = highest % searchedMultiple; // searching Highest horizontal bar by multiple of 5
    const highestHbar = highest - reste + searchedMultiple; // Highest horizontal bar
    highest = highestHbar;
    const hbarCount = 5 // Number or horizontal bars

    return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient  id="Gradient1">
                <stop offset="0%" stopColor={theme.palette.primary.light} />
                <stop offset="15%" stopColor={theme.palette.primary.main} />
                <stop offset="100%" stopColor={theme.palette.primary.dark   } />
            </linearGradient >
        </defs>
        <g transform={`translate(${50}, ${height - 40})`}>
            {Array.from(Array(hbarCount)).map((a, i) => <path key={`hor-bar-${i}`} d={`M0 -${((highestHbar * (i + 1) / hbarCount) / highest) * maxHeight} H${barSpacing * 6 + barWidth * 5}`} stroke="#BDBDBD" strokeDasharray="2 2"  />)}
            {Array.from(Array(hbarCount + 1)).map((a, i) => renderAxisLabel([-18, -((highestHbar * (i) / hbarCount) / highest) * maxHeight], `${Utilities.toFriendlyMoneyValue(highestHbar * (i) / hbarCount)}`, theme.palette.leo.tone14))}
            <rect x={leftMargin} y={-maxHeight * (valueCat1 / highest)} width={barWidth} height={maxHeight * (valueCat1 / highest) + 1} fill={theme.palette.leo.primary.blue[100]} />
            <rect x={leftMargin + barSpacing + barWidth} y={-maxHeight * (valueCat2 / highest)} width={barWidth} height={maxHeight * (valueCat2 / highest) + 1} fill={theme.palette.leo.primary.blue[200]} />
            <rect x={leftMargin + barSpacing * 2 + barWidth * 2} y={-maxHeight * (valueCat3 / highest)} width={barWidth} height={maxHeight * (valueCat3 / highest) + 1} fill={theme.palette.leo.primary.blue[300]} />
            <rect x={leftMargin + barSpacing * 3 + barWidth * 3} y={-maxHeight * (valueCat4 / highest)} width={barWidth} height={maxHeight * (valueCat4 / highest) + 1} fill={theme.palette.leo.primary.blue[400]} />
            <rect x={leftMargin + barSpacing * 4 + barWidth * 4} y={-maxHeight * (valueCat5 / highest)} width={barWidth} height={maxHeight * (valueCat5 / highest) + 1} fill={theme.palette.primary.dark} />
            {renderLabel([leftMargin + barWidth / 2, -maxHeight * (valueCat1 / highest) / 2], cohort1.length, theme.palette.leo.primary.blue[100], theme.palette.background.paper)}
            {renderLabel([leftMargin + barSpacing + barWidth * 3 / 2, -maxHeight * (valueCat2 / highest) / 2], cohort2.length, theme.palette.leo.primary.blue[200], theme.palette.background.paper)}
            {renderLabel([leftMargin + barSpacing * 2 + barWidth * 5 / 2, -maxHeight * (valueCat3 / highest) / 2], cohort3.length, theme.palette.leo.primary.blue[300], theme.palette.background.paper)}
            {renderLabel([leftMargin + barSpacing * 3 + barWidth * 7 / 2, -maxHeight * (valueCat4 / highest) / 2], cohort4.length, theme.palette.leo.primary.blue[400], theme.palette.background.paper)}
            {renderLabel([leftMargin + barSpacing * 4 + barWidth * 9 / 2, -maxHeight * (valueCat5 / highest) / 2], cohort5.length, theme.palette.primary.dark, theme.palette.background.paper)}
            <rect x={0} y={6} width={leftMargin*2 + barSpacing * 4 + barWidth * 5} height={8} fill="url(#Gradient1)" rx={4} ry={4}/>
            <rect x={leftMargin + barSpacing*1/2 + barWidth} y={6} width={1} height={8} fill={theme.palette.common.white} />
            <rect x={leftMargin + barSpacing*3/2 + barWidth*2} y={6} width={1} height={8} fill={theme.palette.common.white} />
            <rect x={leftMargin + barSpacing*5/2 + barWidth*3} y={6} width={1} height={8} fill={theme.palette.common.white} />
            <rect x={leftMargin + barSpacing*7/2 + barWidth*4} y={6} width={1} height={8} fill={theme.palette.common.white} />
            {renderBottomLabel([leftMargin + barSpacing*1/2 + barWidth,30], 300, theme.palette.leo.tone13)}
            {renderBottomLabel([leftMargin + barSpacing*3/2 + barWidth*2,30], 1000, theme.palette.leo.tone13)}
            {renderBottomLabel([leftMargin + barSpacing*5/2 + barWidth*3,30], 4000, theme.palette.leo.tone13)}
            {renderBottomLabel([leftMargin + barSpacing*7/2 + barWidth*4,30], 10000, theme.palette.leo.tone13)}
        </g>
    </svg>;
}

export default InvestmentsGraph;